import {
  collection,
  doc,
  onSnapshot,
  query,
  where,
  type Timestamp,
} from "firebase/firestore";
import { db } from "./firebase.ts";

export type WebinarStatus = "upcoming" | "live" | "processing" | "vod";

export interface WebinarConfig {
  title: string;
  sort_index?: number;
  stream_id?: string;
  vod_video_id?: string;
  live_input_uid?: string;
  scheduled_at?: Timestamp;
  status?: WebinarStatus;
  live?: boolean;
}

export interface Launch {
  title: string;
  status: "draft" | "waitlist" | "enrollment_open" | "active" | "completed";
  webinars?: WebinarConfig[];
}

export type PickedWebinar =
  | { kind: "live"; webinar: WebinarConfig; videoId: string }
  | { kind: "upcoming"; webinar: WebinarConfig; scheduledAt: Date }
  | { kind: "vod"; webinar: WebinarConfig; videoId: string }
  | { kind: "processing"; webinar: WebinarConfig }
  | { kind: "none" };

/**
 * Select which webinar to show for a given launch.
 *
 * Order:
 *   1. Any webinar currently live
 *   2. Earliest upcoming (we show a countdown)
 *   3. Most recent VOD (already finished, playback available)
 *   4. Any webinar in "processing" (recording is being prepared)
 */
export function pickWebinar(launch: Launch): PickedWebinar {
  const webinars = (launch.webinars ?? []).slice();
  if (webinars.length === 0) return { kind: "none" };

  const live = webinars.find((w) => w.status === "live" && w.live_input_uid);
  if (live) return { kind: "live", webinar: live, videoId: live.live_input_uid! };

  const upcoming = webinars
    .filter((w) => w.status === "upcoming" && w.scheduled_at)
    .sort((a, b) => a.scheduled_at!.toMillis() - b.scheduled_at!.toMillis())[0];
  if (upcoming) {
    return {
      kind: "upcoming",
      webinar: upcoming,
      scheduledAt: upcoming.scheduled_at!.toDate(),
    };
  }

  const vod = webinars
    .filter((w) => w.status === "vod" && w.vod_video_id)
    .sort(
      (a, b) =>
        (b.scheduled_at?.toMillis() ?? 0) - (a.scheduled_at?.toMillis() ?? 0)
    )[0];
  if (vod) return { kind: "vod", webinar: vod, videoId: vod.vod_video_id! };

  const processing = webinars.find((w) => w.status === "processing");
  if (processing) return { kind: "processing", webinar: processing };

  return { kind: "none" };
}

export type LaunchSnapshot =
  | { kind: "loading" }
  | { kind: "not_found" }
  | { kind: "ready"; launch: Launch; picked: PickedWebinar };

export function watchLaunch(
  launchId: string,
  cb: (snap: LaunchSnapshot) => void
): () => void {
  cb({ kind: "loading" });
  return onSnapshot(
    doc(db, "launches", launchId),
    (snap) => {
      if (!snap.exists()) {
        cb({ kind: "not_found" });
        return;
      }
      const launch = snap.data() as Launch;
      cb({ kind: "ready", launch, picked: pickWebinar(launch) });
    },
    (err) => {
      console.error("launches snapshot error", err);
      cb({ kind: "not_found" });
    }
  );
}

export type ActiveLaunchSnapshot =
  | { kind: "loading" }
  | { kind: "none" }
  | { kind: "ready"; launchId: string; launch: Launch; picked: PickedWebinar };

const PICK_PRIORITY: Record<PickedWebinar["kind"], number> = {
  live: 0,
  upcoming: 1,
  vod: 2,
  processing: 3,
  none: 4,
};

/**
 * Real-time listener over all currently-active launches.
 * Emits the single launch+webinar best suited for the root URL,
 * prioritising live > upcoming (earliest) > vod > processing.
 */
export function watchActiveLaunch(
  cb: (snap: ActiveLaunchSnapshot) => void
): () => void {
  cb({ kind: "loading" });
  const q = query(
    collection(db, "launches"),
    where("status", "in", ["active", "enrollment_open"])
  );
  return onSnapshot(
    q,
    (snap) => {
      if (snap.empty) {
        cb({ kind: "none" });
        return;
      }
      const candidates = snap.docs.map((d) => ({
        launchId: d.id,
        launch: d.data() as Launch,
        picked: pickWebinar(d.data() as Launch),
      }));
      candidates.sort((a, b) => {
        const diff = PICK_PRIORITY[a.picked.kind] - PICK_PRIORITY[b.picked.kind];
        if (diff !== 0) return diff;
        if (a.picked.kind === "upcoming" && b.picked.kind === "upcoming") {
          return a.picked.scheduledAt.getTime() - b.picked.scheduledAt.getTime();
        }
        return 0;
      });
      const best = candidates[0];
      if (best.picked.kind === "none") {
        cb({ kind: "none" });
        return;
      }
      cb({ kind: "ready", ...best });
    },
    (err) => {
      console.error("active launches snapshot error", err);
      cb({ kind: "none" });
    }
  );
}
